import { useEffect, useMemo, useState } from 'react'
import { onValue, ref } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import { TV_BG_GREEN } from '../config/tvTheme.js'
import { boardTitle } from '../lib/boardTitle.js'
import TvFlightBracket from './TvFlightBracket.jsx'
import TvStage from './TvStage.jsx'

// TV bracket board — a trophy header with the tournament name, then the flight
// brackets below. ONE flight shows FULL screen by itself; MULTIPLE flights show
// TWO side by side, hold ~5s, then auto-roll to the next two, cycling through
// all of them and looping. PBCC cream/crimson/dark-green scheme.
const PBCC_GOLD = PBCC_PREMIUM ? 'var(--ge-gold)' : '#FFFFFF'
const CARD_BG = '#0E3320'   // flight card — deep green, a touch lighter than the board
const TILE_BG = PBCC_PREMIUM ? '#0E281D' : 'var(--ge-primary)'   // board background

// Two brackets side by side per page, ~5s each, cycling + looping.
const SET_SIZE = 2
const ROTATE_MS = 5000

// `embedded` fits the component to its parent box (the TV Monitor preview
// tile) instead of taking over the viewport. Standalone (/tv/brackets) leaves
// it false so the screen fills the TV, clean, brackets only.
export default function TvBracketGrid({ tournamentIdProp = null, embedded = false }) {
  const [info, setInfo] = useState(null)
  const [flights, setFlights] = useState({})
  const [brackets, setBrackets] = useState({})
  const tid = tournamentIdProp

  useEffect(() => {
    if (!tid) return undefined
    const offs = [
      onValue(tref(`tournaments/${tid}/info`), (s) => setInfo(s.val() || {})),
      onValue(tref(`tournaments/${tid}/flights`), (s) => setFlights(s.val() || {})),
      onValue(tref(`tournaments/${tid}/brackets`), (s) => setBrackets(s.val() || {})),
    ]
    return () => offs.forEach((o) => o())
  }, [tid])

  const flightList = useMemo(() => (
    Object.entries(flights || {})
      .filter(([id]) => !String(id).startsWith('_'))
      .map(([id, f]) => ({ id, ...f }))
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  ), [flights])

  // Split into sets of 6 in sorted order — Set A = Championship + Flights 1-5,
  // Set B = Flights 6-11 — and rotate between them with a smooth crossfade.
  const sets = useMemo(() => {
    const out = []
    for (let i = 0; i < flightList.length; i += SET_SIZE) out.push(flightList.slice(i, i + SET_SIZE))
    return out
  }, [flightList])

  // Bump a tick every ROTATE_MS. The interval is created ONCE on mount (empty
  // deps) so unrelated re-renders — the flights/brackets/info listeners fire
  // repeatedly — can't tear it down and reset the 12s countdown before it
  // fires. The visible set is derived from tick at render time, so it always
  // tracks the current data even though the timer never restarts.
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), ROTATE_MS)
    return () => clearInterval(id)
  }, [])

  if (!tid) return <Centered embedded={embedded}>No active tournament.</Centered>
  if (flightList.length === 0) return <Centered embedded={embedded}>No flights yet.</Centered>

  const idx = sets.length ? tick % sets.length : 0

  // Trophy header on top; below it the two 3x2 sets crossfade in place.
  // Full-screen TV uses the TV-only green; the embedded admin previews keep the
  // app green so the Bracket tab / monitor tiles are unchanged.
  const boardBg = embedded ? TILE_BG : TV_BG_GREEN
  // Flight-panel green: full-screen TV uses the SAME green as the board/header
  // (TV_BG_GREEN) so the whole board is one uniform green with no lighter middle
  // section — the flight panels are defined by their gold hairline, not a tint.
  // The admin Bracket tab / embedded previews keep the original panel green.
  const panelBg = embedded ? CARD_BG : TV_BG_GREEN
  return (
    <TvStage embedded={embedded} background={boardBg}>
      <div className="w-full h-full flex flex-col overflow-hidden"
           style={{ backgroundColor: boardBg, fontFamily: 'Marcellus, Georgia, serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Marcellus&display=swap');`}</style>
      <TrophyHeader name={boardTitle(info?.name)} />
      <div className="relative flex-1 min-h-0">
        {/* Render ONLY the active set (page). Mounting BOTH pages at once left
            the hidden page's SVG bracket text unpainted on the TV's weaker
            renderer, so page 2 (flights 6-11) showed empty name boxes even
            though the data was present (Mac rendered both fine). Keying the
            container by `idx` remounts the set on each rotation, so the visible
            page is always freshly painted — and only ~6 brackets are in the DOM
            at a time instead of ~12.
            NO opacity fade/transition: the Hisense (Vidaa) TV browser did not
            resolve the opacity animation to 1 over the scaled board, leaving the
            whole page semi-transparent so the dark-green background composited
            through the white boxes → grey. Hard cut at explicit opacity:1 keeps
            every card solid, fully-opaque #FFFFFF. */}
        {(() => {
          const set = sets[idx] || []
          // One flight on the page → full width (full screen). Two → side by side.
          const setCols = set.length <= 1 ? 1 : 2
          return (
            <div
              key={idx}
              className="absolute inset-0 grid gap-2 p-2"
              style={{
                opacity: 1,
                gridTemplateColumns: `repeat(${setCols}, minmax(0, 1fr))`,
                gridTemplateRows: '1fr',
              }}>
              {set.map((flight) => (
                <BracketTile key={flight.id} flight={flight} bracket={brackets?.[flight.id] || null} panelBg={panelBg} />
              ))}
            </div>
          )
        })()}
      </div>
      </div>
    </TvStage>
  )
}

function TrophyHeader({ name }) {
  return (
    <div className="shrink-0 relative flex flex-col items-center justify-center"
         style={{ height: 96, borderBottom: `1px solid ${PBCC_GOLD}44` }}>
      <img src={CLUB.logoLight} alt="PBCC"
           className="absolute left-5 top-1/2 -translate-y-1/2 object-contain"
           style={{ height: 92 }} />
      <div className="flex items-center gap-3">
        <span style={{ fontSize: '1.7rem' }}>🏆</span>
        <h1 className="text-2xl uppercase tracking-wide" style={{ color: PBCC_GOLD }}>
          {name || 'Championship'}
        </h1>
      </div>
      <div className="text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: `${PBCC_GOLD}cc` }}>
        Championship Brackets
      </div>
    </div>
  )
}

function BracketTile({ flight, bracket, panelBg = CARD_BG }) {
  // A bordered flight card with a centered gold FLIGHT title flanked by gold
  // rule lines (like the mockup). The DAY 1/2/3 labels live inside the bracket
  // SVG as gold boxes over their columns.
  return (
    <div className="flex flex-col overflow-hidden rounded-md"
         style={{ backgroundColor: panelBg, border: `1px solid ${PBCC_GOLD}` }}>
      <div className="shrink-0 flex items-center gap-2 px-3 py-0.5">
        <span className="flex-1 h-px" style={{ backgroundColor: PBCC_GOLD }} />
        <span className="text-[22px] uppercase tracking-[0.16em] whitespace-nowrap" style={{ color: PBCC_GOLD }}>
          {flight.info?.name || flight.id}
        </span>
        <span className="flex-1 h-px" style={{ backgroundColor: PBCC_GOLD }} />
      </div>
      <div className="flex-1 min-h-0 px-1 pb-1">
        {bracket
          ? <TvFlightBracket flight={flight} bracket={bracket} />
          : <div className="w-full h-full flex items-center justify-center">
              <span className="text-sm" style={{ color: '#FFFFFF' }}>No bracket yet</span>
            </div>}
      </div>
    </div>
  )
}

function Centered({ children, embedded = false }) {
  return (
    <div className={`${embedded ? 'w-full h-full' : 'fixed inset-0'} flex items-center justify-center`}
         style={{ backgroundColor: TILE_BG, color: PBCC_GOLD }}>
      <p className="text-xl font-bold">{children}</p>
    </div>
  )
}
